import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CreditCard, ShoppingCart, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { settingsCache } from '@/utils/settingsCache';

interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  price: number;
  enabled: boolean;
  description?: string;
}

declare global {
  interface Window {
    paypal: any;
  }
}

export default function CreditPackages() {
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [paypalEnabled, setPaypalEnabled] = useState(false);
  const [paypalClientId, setPaypalClientId] = useState('');
  const [paypalSandboxClientId, setPaypalSandboxClientId] = useState('');
  const [sandboxMode, setSandboxMode] = useState(false);
  const [processing, setProcessing] = useState<string | null>(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkError, setSdkError] = useState<string | null>(null);
  const [sdkLoading, setSdkLoading] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    fetchPackagesAndSettings();
  }, []);

  const fetchPackagesAndSettings = async () => {
    try {
      setLoading(true);
      
      // Fetch PayPal settings
      const [enabled, clientId, sandboxClientId, sandbox, packagesData] = await Promise.all([
        settingsCache.getSetting('paypal_enabled', false),
        settingsCache.getSetting('paypal_client_id', ''),
        settingsCache.getSetting('paypal_sandbox_client_id', ''),
        settingsCache.getSetting('paypal_sandbox_mode', false),
        settingsCache.getSetting('paypal_credit_packages', '[]')
      ]);

      setPaypalEnabled(enabled);
      setPaypalClientId(clientId);
      setPaypalSandboxClientId(sandboxClientId);
      setSandboxMode(sandbox);

      // Use packages data (already parsed by settingsCache)
      const parsedPackages: CreditPackage[] = Array.isArray(packagesData) ? packagesData : [];
      setPackages(parsedPackages.filter(pkg => pkg.enabled));

      // Load PayPal SDK if enabled and we have the right credentials
      const activeClientId = sandbox ? sandboxClientId : clientId;
      if (enabled && activeClientId) {
        loadPayPalSDK(activeClientId, sandbox);
      }
    } catch (error) {
      console.error('Error fetching packages and settings:', error);
      toast({
        title: "Error",
        description: "Failed to load credit packages",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadPayPalSDK = (clientId: string, sandbox: boolean) => {
    if (window.paypal) {
      setSdkReady(true);
      return;
    }

    if (sdkLoading) {
      return; // Prevent multiple simultaneous loads
    }

    setSdkLoading(true);
    setSdkError(null);

    const script = document.createElement('script');
    // Use different PayPal SDK URLs for sandbox vs production
    const baseUrl = sandbox ? 'https://www.sandbox.paypal.com' : 'https://www.paypal.com';
    script.src = `${baseUrl}/sdk/js?client-id=${clientId}&currency=USD&intent=capture${sandbox ? '&vault=false&debug=true' : ''}`;
    script.async = true;
    
    script.onload = () => {
      setSdkReady(true);
      setSdkLoading(false);
      setSdkError(null);
    };

    script.onerror = (error) => {
      console.error('Failed to load PayPal SDK:', error);
      const errorMsg = 'Failed to load PayPal payment system. Please check your internet connection and try again.';
      setSdkError(errorMsg);
      setSdkLoading(false);
      toast({
        title: "PayPal Loading Error",
        description: errorMsg,
        variant: "destructive",
      });
    };

    // Add timeout for slow connections
    setTimeout(() => {
      if (sdkLoading && !window.paypal) {
        console.warn('PayPal SDK load timeout');
        setSdkError('PayPal is taking too long to load. Please refresh the page and try again.');
        setSdkLoading(false);
      }
    }, 10000);

    document.head.appendChild(script);
  };

  const handlePurchase = async (pkg: CreditPackage) => {
    if (!sdkReady || !window.paypal) {
      toast({
        title: "PayPal Not Ready",
        description: sdkError || "PayPal is still loading. Please wait a moment and try again.",
        variant: "destructive",
      });
      return;
    }

    setProcessing(pkg.id);

    try {
      const paypal = window.paypal;
      
      paypal.Buttons({
        createOrder: (data: any, actions: any) => {
          return actions.order.create({
            purchase_units: [{
              amount: {
                value: pkg.price.toFixed(2),
                currency_code: 'USD'
              },
              description: `${pkg.name} - ${pkg.credits} credits`,
              custom_id: JSON.stringify({
                userId: user?.id,
                packageId: pkg.id,
                credits: pkg.credits
              })
            }]
          });
        },
        onApprove: async (data: any, actions: any) => {
          try {
            const order = await actions.order.capture();
            
            // Store purchase details for success modal
            localStorage.setItem('paypal_purchase_success', JSON.stringify({
              credits: pkg.credits,
              amount: pkg.price,
              packageName: pkg.name,
              timestamp: Date.now()
            }));
            
            // The webhook will handle the credit addition
            toast({
              title: "Purchase Successful!",
              description: `Redirecting to your credits page...`,
            });
            
            // Redirect to profile to see updated balance
            navigate('/profile?tab=credits');
          } catch (error) {
            console.error('Error capturing PayPal order:', error);
            toast({
              title: "Payment Error",
              description: "Payment was processed but there was an error. Please contact support.",
              variant: "destructive",
            });
          }
        },
        onError: (err: any) => {
          console.error('PayPal error:', err);
          toast({
            title: "Payment Error",
            description: "There was an error processing your payment. Please try again.",
            variant: "destructive",
          });
        },
        onCancel: () => {
          toast({
            title: "Payment Cancelled",
            description: "You cancelled the payment process.",
          });
        }
      }).render(`#paypal-button-${pkg.id}`);
    } catch (error) {
      console.error('Error setting up PayPal:', error);
      toast({
        title: "Error",
        description: "Failed to initialize payment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-muted rounded w-48 mb-2"></div>
            <div className="h-4 bg-muted rounded w-96 mb-8"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-64 bg-muted rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="sm" onClick={() => navigate('/profile?tab=credits')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Profile
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Buy Credits</h1>
            <p className="text-muted-foreground">Purchase additional credits that never expire</p>
          </div>
        </div>

        {/* Sandbox Mode Indicator */}
        {sandboxMode && paypalEnabled && (
          <Alert className="mb-6 border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950">
            <AlertCircle className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-yellow-800 dark:text-yellow-200">
              <strong>SANDBOX MODE:</strong> This is a testing environment. No real money will be charged.
            </AlertDescription>
          </Alert>
        )}

        {!paypalEnabled && (
          <Alert className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Credit purchasing is currently disabled. Please contact support for assistance.
            </AlertDescription>
          </Alert>
        )}

        {sdkError && paypalEnabled && (
          <Alert className="mb-6 border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800 dark:text-red-200">
              <strong>PayPal Error:</strong> {sdkError}
              <Button 
                variant="outline" 
                size="sm" 
                className="ml-2" 
                onClick={() => window.location.reload()}
              >
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {sdkLoading && paypalEnabled && (
          <Alert className="mb-6 border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950">
            <AlertCircle className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-blue-800 dark:text-blue-200">
              <strong>Loading PayPal:</strong> Payment system is initializing...
            </AlertDescription>
          </Alert>
        )}

        {/* Credit Packages */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {packages.map((pkg) => (
            <Card key={pkg.id} className="relative">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{pkg.name}</span>
                  <Badge variant="secondary">
                    ${pkg.price}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  {pkg.description || `Get ${pkg.credits.toLocaleString()} credits for your calling campaigns`}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary">
                    {pkg.credits.toLocaleString()}
                  </div>
                  <div className="text-sm text-muted-foreground">Credits</div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Never expire
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Used last in priority order
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    Instant delivery
                  </div>
                </div>

                <div className="border-t pt-4">
                  {paypalEnabled ? (
                    <div>
                      <Button
                        onClick={() => handlePurchase(pkg)}
                        disabled={processing === pkg.id || !sdkReady || sdkLoading || !!sdkError}
                        className="w-full mb-3"
                      >
                        <ShoppingCart className="h-4 w-4 mr-2" />
                        {processing === pkg.id ? 'Setting up...' : 
                         sdkLoading ? 'Loading PayPal...' :
                         sdkError ? 'PayPal Error' :
                         !sdkReady ? 'PayPal Loading...' :
                         `Buy for $${pkg.price}`}
                      </Button>
                      <div id={`paypal-button-${pkg.id}`}></div>
                    </div>
                  ) : (
                    <Button disabled className="w-full">
                      <CreditCard className="h-4 w-4 mr-2" />
                      Purchase Unavailable
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {packages.length === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-muted-foreground mb-2">
                No credit packages available
              </h3>
              <p className="text-sm text-muted-foreground">
                Credit packages haven't been configured yet. Please contact support for assistance.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Information Card */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5" />
              How Credit Purchasing Works
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <div>• <strong>Instant Delivery:</strong> Credits are added to your account immediately after purchase</div>
            <div>• <strong>Never Expire:</strong> Purchased credits don't expire and are used last in the priority order</div>
            <div>• <strong>Secure Payment:</strong> All transactions are processed securely through PayPal</div>
            <div>• <strong>Usage Priority:</strong> Daily → Monthly → Rollover → Bonus → Purchased credits</div>
            
          </CardContent>
        </Card>
      </div>
    </div>
  );
}